// @ts-nocheck

import type { Location } from 'react-router-dom';

import { Target } from '../../../client';
import type { CustomRouterHistory } from '../../../createHistory';
import Formatter from '../../../i18n/formatter';
import { __, getCurrency } from '../../../i18n/l10n';
import { type StateType } from '../../../states';
import Grid from '../states/grid';
import Pool from '../states/pool';
import Selection from '../states/selection';
import { GridTypes, PoolStatuses } from '../types';
import { parsePreChecksFromUrl } from '../utils';
import { isGridPublic, isPool, isGridsHistory, isGridShare } from '../utils/routes';

import { getGrid } from './grids';
import { getGridTotalAmount } from './grids';
import { getPool } from './pool';

export const getBasketState = (state: StateType) => state.grids.basket;

export const getIsSystem = (state: StateType) => {
  const { isSystem } = getBasketState(state);
  return isSystem;
};

export const getPrivateToogle = (state: StateType) => ({
  isPrivate: getBasketState(state).isPrivate,
});

export const getBetAmount = (state: StateType) => {
  const { betAmount } = getBasketState(state);
  const amount = parseInt(betAmount, 10);
  return isNaN(amount) ? 0 : amount;
};

export const getBasketButtonIsDisabled = (state: StateType) => {
  const { selections, isSubmitting, gridType, betAmount, isSystem, gridReference } = getBasketState(state);
  if (isSubmitting) {
    return true;
  }

  const isFunding = !!gridReference;
  if (isFunding) {
    const noStake = betAmount === '';
    if (noStake) {
      return true;
    }
    const grid = getGrid(state, gridReference);
    if (!Grid.isFunding(grid) || Grid.isStakeTooHigh(grid, parseFloat(betAmount))) {
      return true;
    }
  } else {
    if (!Selection.hasEnoughChecksForAllSelections(selections)) {
      return true;
    }
    if (gridType === GridTypes.TEAM) {
      const gridAmount = selections.reduce((acc, selection) => acc + Selection.getAmount(selection), 0);
      const isStakeTooHigh = parseFloat(betAmount) > gridAmount - 1;
      if (isStakeTooHigh) {
        return true;
      }
      if (!isSystem) {
        const selection = selections[0];
        const countDouble = Selection.getCountDoubles(selection);
        const countTriple = Selection.getCountTriples(selection);
        const hasOnlyOneCheckPerLine = countDouble === 0 && countTriple === 0;
        if (hasOnlyOneCheckPerLine) {
          return true;
        }
      }
    }
  }
  return false;
};

export const getBasketIsValidated = (state: StateType) => {
  const { estr, isValidated } = getBasketState(state);
  return !estr && isValidated;
};

export const getBasketTotalAmount = (state: StateType) => {
  const { selections } = getBasketState(state);
  return selections.reduce((acc, selection) => (selection ? acc + Selection.getAmount(selection) : acc), 0);
};

export const getGridsBasketViewSelector = (state: StateType) => {
  const { gridReference, estr, poolId: storePoolId, isSubmitting } = getBasketState(state);

  let poolId = storePoolId;
  let isAvailable = false;
  let isEmpty = false;

  const isValidated = getBasketIsValidated(state);

  if (!isValidated && gridReference) {
    const grid = getGrid(state, gridReference);
    poolId = grid && grid.poolId;
  }

  let isPoolOpen = false;
  if (poolId) {
    const pool = getPool(state, poolId);
    const poolStatus = pool?.poolStatus || null;
    isPoolOpen = poolStatus === PoolStatuses.OPEN;
    isEmpty = !isPoolOpen;
  } else {
    isEmpty = true;
  }

  isAvailable = isPoolOpen;
  if (gridReference) {
    const grid = getGrid(state, gridReference);
    isAvailable = Grid.isFunding(grid) || isValidated; // isValidated because show basket if last funding bet
  }

  return {
    isSubmitting,
    isValidated,
    errorLabel: estr,
    gridReference,
    isAvailable,
    isFunding: !!gridReference,
    isEmpty,
    poolId,
    poolNumber: poolId && Pool.getNumber(poolId),
  };
};

export const getValidatedViewSelector = (state: StateType) => {
  const { gridReference, gridType } = getBasketState(state);
  return {
    gridReference,
    stake: getBetAmount(state),
    gridPrice: gridReference ? getGridTotalAmount(state, gridReference) : getBasketTotalAmount(state),
    gridType,
  };
};

const isPublicBasketDesktopHide = (location: Location): boolean => {
  return !Target.__MOBILE__ && isGridPublic(location.pathname) && location.hash.indexOf('#cart') === -1;
};

export const getGridsBasketContent = (state: StateType, { location }: { location: Location }) => {
  const { gridReference, gridType: basketGridType, isSystem, poolId: basketPoolId } = getBasketState(state);

  const shouldHideBasket = isPublicBasketDesktopHide(location);

  const isFunding = !!gridReference;
  let poolId = basketPoolId;
  const gridType = basketGridType;
  let allBetAmount = null;

  const amountLabel = isSystem ? __('System grid price') : __('Grid price');
  let amount = getBasketTotalAmount(state);

  if (isFunding) {
    const grid = getGrid(state, gridReference);
    if (grid) {
      const { allBetAmount: gridAllBetAmount, poolId: gridPoolId } = grid;
      allBetAmount = gridAllBetAmount;
      poolId = gridPoolId;
      amount = Grid.getAmount(grid);
    }
  }

  const poolNumber = poolId ? Pool.getNumber(poolId) : null;

  return {
    amount,
    gridType,
    allBetAmount,
    amountLabel,
    poolNumber,
    isSystem,
    gridReference,
    isFunding,
    shouldHideBasket,
  };
};

export const getBasketStakeSelector = (state: StateType) => {
  const { gridReference, gridType: basketGridType, betAmount } = getBasketState(state);

  const isFunding = !!gridReference;
  let allBetAmount = null;

  const currency = getCurrency();
  let stakeLabel = __('My wager');
  let amount = getBasketTotalAmount(state);

  let maxStake = amount - 1;
  if (!isFunding && basketGridType === GridTypes.TEAM && amount > 1) {
    stakeLabel += ' ';
    stakeLabel += __('(1 to %1$s)', Formatter.formatNumber(maxStake, currency, false, true));
  }

  if (isFunding) {
    const grid = getGrid(state, gridReference);
    if (grid) {
      const { allBetAmount: gridAllBetAmount } = grid;
      allBetAmount = gridAllBetAmount;
      amount = Grid.getAmount(grid);
      stakeLabel += ' ';
      maxStake = amount - allBetAmount;
      stakeLabel += __('(1 to %1$s)', Formatter.formatNumber(amount - allBetAmount, currency, false, true));
    }
  }

  const stake = parseInt(betAmount || 1, 10);

  return {
    maxStake,
    stake,
    stakeLabel,
  };
};

export const getBasketButton = (state: StateType) => {
  const { connected: isConnected } = state.user;
  const { poolId: storePoolId, gridReference } = getBasketState(state);
  let poolId = storePoolId;
  if (gridReference) {
    const grid = getGrid(state, gridReference);
    poolId = grid?.poolId;
  }

  const isDisabled = getBasketButtonIsDisabled(state);
  const isValidated = getBasketIsValidated(state);

  return {
    poolId,
    isDisabled,
    isConnected,
    isValidated,
  };
};

export const getBasketGridSelector = (state: StateType, { gridReference }: { gridReference: string }) => {
  const { selections: basketSelections, isSystem: basketIsSystem } = getBasketState(state);
  let pool = null;
  let selections = null;
  let isSystem = false;

  const isFunding = !!gridReference;
  const grid = gridReference && getGrid(state, gridReference);
  if (isFunding && grid) {
    const { poolId, isSystem: isGridSystem, systemStrGridSelections, strGridSelection } = grid;
    pool = getPool(state, poolId);
    isSystem = isGridSystem;
    if (systemStrGridSelections && isSystem) {
      const compactSelection = Selection.getCompactSystem(systemStrGridSelections);
      selections = [compactSelection, ...systemStrGridSelections];
    } else {
      selections = [strGridSelection];
    }
  } else {
    isSystem = basketIsSystem;
    if (isSystem) {
      // first selection is a compact view
      const compactSelection = basketSelections ? Selection.getCompactSystem(basketSelections) : [];
      selections = [compactSelection, ...basketSelections];
    } else {
      selections = basketSelections;
    }
    const { poolId } = getBasketState(state);
    pool = poolId && getPool(state, poolId);
  }

  // $FlowFixMe
  const matches = pool?.matches || [];

  return {
    matches,
    selections,
    isSystem,
  };
};

type LocationMatch = {
  params: {
    poolTitle: string;
    gridReference: string;
  };
};

// if we enter pool, public, history or share route, we init basket
export const getUpdateBasketSelector = (
  state: StateType,
  { match, location }: { match: LocationMatch; history: CustomRouterHistory; location: Location },
) => {
  let selections = null;
  let poolId = null;
  let gridReference = null;

  let shoudInit = false;
  let shoudPublicInit = false;
  let shoudHistoryInit = false;
  let shouldFillPrechecks = false;

  const isPoolRoute = isPool(location.pathname);
  const isHistoryRoute = isGridsHistory(location.pathname);
  const isPublicRoute = isGridPublic(location.pathname);
  const isShareRoute = isGridShare(location.pathname);

  const urlPoolTitle = match.params?.poolTitle || null;
  const urlGridReference = match.params?.gridReference || null;
  const urlPoolId = (urlPoolTitle && Pool.getIdFromUrl(urlPoolTitle)) || null;
  const { poolId: basketPoolId, selections: basketSelections } = getBasketState(state);

  if (isPoolRoute) {
    const pool = getPool(state, urlPoolId);
    if (pool && !pool.isFetching) {
      const matchLength = pool.matches?.length;
      const preChecks = parsePreChecksFromUrl(matchLength);
      if (preChecks) {
        selections = preChecks;
        shouldFillPrechecks = true;
        poolId = urlPoolId;
      } else if (urlPoolId && urlPoolId !== basketPoolId) {
        poolId = urlPoolId;
        selections = [Selection.createEmpty(matchLength)];
        shoudInit = true;
      } else if (basketSelections.length === 0) {
        selections = [Selection.createEmpty(matchLength)];
        shoudInit = true;
      }
    }
  } else if ((isPublicRoute || isHistoryRoute || isShareRoute) && urlGridReference) {
    gridReference = urlGridReference;
    const pool = getGrid(state, urlGridReference);
    poolId = pool?.poolId || null;
    shoudPublicInit = isPublicRoute || isShareRoute;
    shoudHistoryInit = isHistoryRoute;
  }

  return {
    poolId,
    gridReference,
    selections,
    shoudInit,
    shoudPublicInit,
    shoudHistoryInit,
    shouldFillPrechecks,
    urlPoolTitle,
  };
};
